import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchHyperwalletStatus } from "../../actions/monetization";
import { isMobileDevice } from "../../utils/browserUtils";
import styles from "../../styles/admanager/UserAdManagement.module.scss";
import shared from "../../styles/UserProfileCommon.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons/faChevronUp";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import GenericTable from "../helpers/genericTable";
import {
  getHWAccountStatus,
  getProjects,
  getHWUserInfo,
} from "../../selectors/monetizationSelector";
import { fetchGames } from "../../actions/monetization";
import selectCurrentState from "../../selectors/selectCurrentState";
import selectCurrentUser from "../../selectors/currentUserInfo";
import { WorkState } from "../../reducers/models";
import { HWAccountStatus } from "../../reducers/models";
import Spinner from "../../components/Spinner";
import classnames from "classnames";

export default function UserAdGames(props) {
  const dispatch = useDispatch();
  const games = useSelector(getProjects);
  const userInfo = useSelector(getHWUserInfo);
  const currentUser = useSelector(selectCurrentUser);
  const accountStatus = useSelector(getHWAccountStatus);
  const { workState } = useSelector(selectCurrentState);
  const { status } = accountStatus;

  const [errorMessage, setErrorMessage] = useState(false);
  const [isRegistered, setIsRegistered] = useState(true);
  const isLoading = workState.projectsFetch === WorkState.Loading;

  const wasRegisteredPreviously = status === HWAccountStatus.ACTIVATED && userInfo
  useEffect(() => {
    if (currentUser) {
      const fetchHyperWalletData = async () => {
        await dispatch(fetchHyperwalletStatus());
        if (wasRegisteredPreviously) {
          setIsRegistered(true);
        } else {
          setIsRegistered(false);
        }
      };

      fetchHyperWalletData();
    }
  }, [currentUser, wasRegisteredPreviously, dispatch]);

  useEffect(() => {
    dispatch(fetchGames());
  }, [dispatch]);

  useEffect(() => {
    if (workState.projectsFetch === WorkState.Success) {
      setErrorMessage("");
    } else if (workState.projectsFetch === WorkState.Error) {
      //TODO: should we set error message? or use toast
      setErrorMessage("There was a problem fetching your games");
    }
  }, [workState.projectsFetch]);

  const GAMES_COLUMNS = [
    {
      id: "projectName",
      Header: "Game Name",
      accessor: "projectName",
      Cell: (props) => {
        const { value, row } = props;
        const bbApp = row.original.app;
        let appIcon
        if(bbApp === "buildbox4") {
          appIcon = (
            <img
              alt="Buildbox 4"
              src="https://frontend-assets.buildbox.com/BB4_logo.svg"
              className={styles.appIcon}
            />
          )
        }        
        else if(bbApp === "buildbox2") {
          appIcon = (
            <img
              alt="Buildbox 2"
              src="https://frontend-assets.buildbox.com/BB2_logo.svg"
              className={styles.appIcon}
            />
          )
        }
        else if(bbApp === "buildbox3") {
          appIcon = (
            <img
              alt="Buildbox 3"
              src="https://frontend-assets.buildbox.com/BB3+logo.svg"
              className={styles.appIcon}
            />
          )
        }
        else {
          // bb4 default if unknown
          appIcon = (
            <img
              alt="Buildbox 4"
              src="https://frontend-assets.buildbox.com/BB4_logo.svg"
              className={styles.appIcon}
            />
          )
        }

        if (row.id.includes(".")) {
          return null;
        }
        return (
          <div className={styles.appCellWrapper}>
            {appIcon}
            <span>{value}</span>
          </div>
        );
      },
    },
    {
      Header: "Bundle Id",
      accessor: "bundleId",
    },
    {
      Header: "Platform",
      accessor: "platform",
      Cell: (props) => {
        const { value } = props;

        let appIcon =
          value === "ios" ? (
            <img
              alt="iOS"
              src="https://frontend-assets.buildbox.com/Icon_iOS.svg"
              className={styles.platformIcon}
            />
          ) : (
            <img
              alt="Android"
              src="https://frontend-assets.buildbox.com/Icon_Android.svg"
              className={styles.platformIcon}
            />
          );

        if (value === "2") {
          appIcon = null;
        }

        return (
          <div className={styles.appCellWrapper}>
            {appIcon}
            {value}
          </div>
        );
      },
    },
    {
      Header: "Total Earnings to Date",
      accessor: "lifetimeRevenueCents",
    },
    {
      Header: "Last Updated Date",
      accessor: "lastUpdate",
    },
  ];

  const columns = useMemo(
    () => [
      {
        Header: " ",
        columns: GAMES_COLUMNS,
      },
      {
        id: "expander",
        Header: ({ getToggleAllRowsExpandedProps }) => (
          <span {...getToggleAllRowsExpandedProps()}></span>
        ),
        Cell: ({ row }) =>
          row.canExpand ? (
            <span
              {...row.getToggleRowExpandedProps({
                style: {
                  paddingLeft: `${row.depth * 2}rem`,
                },
              })}
            >
              {isMobileDevice() ? (
                <div className={styles.showHideBundle}>
                  {row.isExpanded ? `Hide Bundles` : `Show Bundles`}
                </div>
              ) : (
                <div className={styles.showHideBundle}>
                  {row.isExpanded ? (
                    <FontAwesomeIcon icon={faChevronUp} />
                  ) : (
                    <FontAwesomeIcon icon={faChevronDown} />
                  )}
                </div>
              )}
            </span>
          ) : null,
      },
    ],
    [GAMES_COLUMNS]
  );

  const data = useMemo(() => games, [games]);

  const tablePaginationProps = {
    showSkipToEnd: false,
    showSkipToBeginning: false,
    showPageSizeDropdown: false,
    showGoToPage: false,
    pageItem: 14,
    rowHeight: 75,
  };
  const rowClickCallback = (row) => {
    console.log("rowClickCallback", row);
  };

  const spinnerStyle = {
    height: "50px",
    width: "50px",
  };

  const renderTable = () => {
    if (isLoading) {
      return (
        <div className={shared.spinnerWrapper}>
          <Spinner isLoading={isLoading} spinnerStyle={spinnerStyle} />
        </div>
      );
    }
    if (workState.projectsFetch === WorkState.None && games.length === 0) {
      return (
        <div className={styles.errorContainer}>
          <p>There are no current game exports</p>
        </div>
      );
    }

    return (
      <GenericTable
        data={data}
        columns={columns}
        rowClickCallback={rowClickCallback}
        tablePaginationProps={tablePaginationProps}
        scrollToOption={true}
      />
    );
  };

  function redirectToSettings() {
    props.history.push("/account/ads/settings");
  }

  const hyperwalletBanner = () => {
    return (
      <div className={styles.registrationBlurbContainer}>
        <div className={styles.imageBlurbContainer}>
          <img
            src="https://frontend-assets.buildbox.com/hyperwallet.png"
            alt="Hyperwallet Logo"
            className={styles.hyperwalletLogo}
          />
          <div>Get paid on your Adbox revenue! Connect to Hyperwallet.</div>
        </div>
        <button onClick={redirectToSettings}>Connect</button>
      </div>
    );
  };
  return (
    <div className={styles.userAdManagementWrapper}>
      {!isRegistered && hyperwalletBanner()}
      <h2 className={classnames(styles.header, styles.gamesPage)}>All Games</h2>
      {errorMessage ? (
        <div className={styles.errorContainer}>
          <p>{errorMessage}</p>{" "}
        </div>
      ) : (
        renderTable()
      )}
    </div>
  );
}
